import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { FeedbackService } from '@app/core/services';
import { TableConfig } from '@app/interfaces';
import { TableComponent, TitleComponent } from '@app/shared/components';
import { FormComponent, FormMode } from '@app/shared/forms/components';
import { AccountStructureService } from '@app/shared/services';
import { CoaImportHeaders } from './coa-import-headers.constant';
import { ImportWizardComponent } from './components/import-wizard/import-wizard.component';
import { AccountImportExampleModel } from './models/AccountImportExampleModel.model';
import { AccountImportModel } from './models/AccountImportModel.model';

@Component({
	selector: 'sl-coa-import-tool',
	standalone: true,
	imports: [MatIconModule, TitleComponent, TableComponent],
	templateUrl: './coa-import-tool.component.html',
	styleUrl: './coa-import-tool.component.less',
	encapsulation: ViewEncapsulation.None,
})
export class CoaImportToolComponent implements OnInit {
	constructor(
		protected readonly dialog: MatDialog,
		private readonly feedbackService: FeedbackService,
		private readonly accountStructureService: AccountStructureService
	) {}
	@ViewChild(FormComponent) formComponent: FormComponent;

	@ViewChild('fileInput', { static: false }) fileInput: ElementRef;

	options: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: true,
			selects: {
				accountTypes: [],
			},
		},
	};
	form = new UntypedFormGroup({});
	fields: FormlyFieldConfig[] = [
		{
			wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimple],
			fieldGroup: [
				{
					key: 'AccountNumber',
					type: FORMLY_FIELD_TYPES.input,
					props: {
						label: 'Account Number',
						required: true,
					},
				},
				{
					key: 'AccountDescription',
					type: FORMLY_FIELD_TYPES.input,
					props: {
						label: 'Description',
						required: true,
					},
				},
				{
					key: 'AccountType',
					type: 'select',
					props: {
						label: 'Account Type',
						required: true,
						options: [
							{
								label: 'Asset',
								value: 'Asset',
							},
							{
								label: 'Liability',
								value: 'Liability',
							},
							{
								label: 'Expense',
								value: 'Expense',
							},
							{
								label: 'Revenue',
								value: 'Revenue',
							},
							{
								label: 'Net',
								value: 'Net',
							},
							{
								label: 'Reserve',
								value: 'Reserve',
							},
						],
					},
				},
				{
					key: 'StartDate',
					type: FORMLY_FIELD_TYPES.datepicker,
					templateOptions: {
						label: 'Start Date',
						required: true,
					},
				},
				{
					key: 'EndDate',
					type: FORMLY_FIELD_TYPES.datepicker,
					props: {
						label: 'End Date',
						required: true,
					},
				},
				{
					key: 'BeginningBalance',
					type: FORMLY_FIELD_TYPES.inputPositiveIntegers,
					props: {
						label: 'Beginning Balance',
						required: true,
					},
				},
			],
		},
	];

	filters = { search: '' };

	data: AccountImportExampleModel[] = [];
	entityData: AccountImportModel[] = [];
	entity: AccountImportModel;

	tableConfig: TableConfig<AccountImportExampleModel> = {
		defaultSortDirection: 'asc',
		showRowSelect: false,
		showRowActions: false,
		defaultSortField: 'AccountNumber',
		columns: [
			{
				header: CoaImportHeaders.AccountNumber,
				key: 'AccountNumber',
			},
			{
				header: CoaImportHeaders.Description,
				key: 'AccountDescription',
			},
			{
				header: CoaImportHeaders.AccountType,
				key: 'AccountType',
			},
			{
				header: CoaImportHeaders.StartDate,
				key: 'StartDate',
			},
			{
				header: CoaImportHeaders.EndDate,
				key: 'EndDate',
			},
			{
				header: CoaImportHeaders.BeginningBalance,
				key: 'BeginningBalance',
			},
		],
	};

	pageTitle = 'Chart of Accounts Import Tool';

	ngOnInit(): void {
		const accountMasks: string[] = [];
		this.accountStructureService.getAccountTypes().subscribe((accountTypes) => {
			if (accountTypes && accountTypes?.accountTypes?.length > 0) {
				for (const accountType of accountTypes.accountTypes) {
					accountType.Mask = accountType.Mask.replace(/0/g, '?');
					const accountTypeDescription = `${accountType.Description} - ${accountType.Mask}`;
					accountMasks.push(accountTypeDescription);
				}
			}

			let accountMaskString = accountMasks.join('\n ');
			accountMaskString = `Account number cannot have duplicates.\n
			Account must be in this format for the specificed type:\n\n
			${accountMaskString}`;
			this.data = [new AccountImportExampleModel(accountMaskString)];
		});
	}

	beginImport() {
		const dialogRef = this.dialog.open(ImportWizardComponent, {
			disableClose: true,
			maxHeight: '90vh',
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result === true) {
				this.feedbackService.customSuccess('Import Successful!');
				this.entityData = [];
			}
		});
	}
}
